import Link from 'next/link';

import type { Translate } from '@/utils/hooks/translation/useTranslation';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';

// Lib
import { DATA_ACCESS, SOCIAL_PROFILES } from '@/lib/routes/privateRoutes';

import { Trans } from '@/components/tools/translation';

export const STATUS = {
    active: 'active',
    inactive: 'inactive',
    reAuth: 'reAuth',
    reAuthSchedulerPerms: 'reAuthSchedulerPerms',
    userTeamSchedulerPerms: 'userTeamSchedulerPerms',
    disconnected: 'disconnected',
    refresh: 'refresh',
    expiringSoon: 'expiringSoon'
} as const;

export type StatusType = keyof typeof STATUS;

export const makeSortOptions = (t: Translate) => [
    {
        value: 'connected',
        label: t('socialprofiles:connected'),
        button: {
            text: t('socialprofiles:connected'),
            icon: 'check',
            iconColor: 'text-icoGreen'
        },
        order: [
            STATUS.active,
            STATUS.reAuth,
            STATUS.reAuthSchedulerPerms,
            STATUS.userTeamSchedulerPerms,
            STATUS.disconnected,
            STATUS.refresh,
            STATUS.expiringSoon,
            STATUS.inactive
        ]
    },
    {
        value: 'disconnected',
        label: t('socialprofiles:disconnected'),
        button: {
            text: t('socialprofiles:disconnected'),
            icon: 'warning',
            iconColor: 'text-icoRed'
        },
        order: [
            STATUS.reAuth,
            STATUS.reAuthSchedulerPerms,
            STATUS.userTeamSchedulerPerms,
            STATUS.disconnected,
            STATUS.refresh,
            STATUS.expiringSoon,
            STATUS.active,
            STATUS.inactive
        ]
    },
    {
        value: 'deactivated',
        label: t('socialprofiles:deactivated'),
        button: {
            text: t('socialprofiles:deactivated'),
            icon: 'disconnect',
            iconColor: 'text-icoGray-500'
        },
        order: [
            STATUS.inactive,
            STATUS.active,
            STATUS.reAuth,
            STATUS.reAuthSchedulerPerms,
            STATUS.userTeamSchedulerPerms,
            STATUS.disconnected,
            STATUS.refresh,
            STATUS.expiringSoon
        ]
    }
];

// The backend sends us only 3 social profile types: connected, warning or error.
// Based on the social profile type, its error message and its active status,
// we assign one of the STATUS to the social profile.
export const makeDisplayedStatus = (socialProfile: SocialProfileType): keyof typeof STATUS => {
    if (!socialProfile.active) return STATUS.inactive;

    const { type, error } = socialProfile.auth_status;

    if (type === 'connected') return STATUS.active;

    if (type === 'warning') {
        if (error === 'token_expiring_5d') {
            return STATUS.refresh;
        } else {
            return STATUS.expiringSoon;
        }
    }

    if (type === 'error') {
        if (error === 'user_re_auth') {
            return STATUS.reAuth;
        } else if (error === 'user_re_auth_scheduler_permissions') {
            return STATUS.reAuthSchedulerPerms;
        } else if (error === 'user_team_scheduler_permissions') {
            return STATUS.userTeamSchedulerPerms;
        } else {
            return STATUS.disconnected;
        }
    }
};

type GetErrorWarningMessageTokenProps = {
    socialProfile: SocialProfileType;
    isAdmin: boolean;
};

export const getErrorWarningMessageToken = ({
    socialProfile,
    isAdmin
}: GetErrorWarningMessageTokenProps) => {
    const status = makeDisplayedStatus(socialProfile);
    let href = SOCIAL_PROFILES;

    if (
        socialProfile.social_network_type === 'instagram' ||
        socialProfile.social_network_type === 'facebook'
    ) {
        if (status === STATUS.disconnected) {
            href = DATA_ACCESS;
        }
    }

    if (status === STATUS.disconnected || status === STATUS.reAuth) {
        if (!isAdmin) {
            return <Trans i18nKey="layout_platform:profiles.error.access_expired_team_member" />;
        }

        return (
            <Trans
                i18nKey="layout_platform:profiles.error.access_expired"
                components={{
                    link: <Link href={href} className="underline" />
                }}
            />
        );
    }

    // for any other status return fallback
    return (
        <Trans
            i18nKey="layout_platform:profiles.error.access_will_expire"
            components={{
                link: <Link href={href} className="underline" />
            }}
        />
    );
};
